// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { Component } from "react";
import onClickOutside from "react-onclickoutside";
import { Text } from "@unifize/sarah";

import { Select } from "@chakra-ui/react";
import { roleTitles } from "src/constants/roleManagement";
import { RelativeBox } from "src/styles/box";
import { Button, Secondary as SecondaryButton } from "src/styles/buttons";
import { BlockFormGroup, Footer, InlineLabel as Label } from "src/styles/form";
import { Input } from "src/styles/input";
import { sanitizeSquareBrackets, validateEmail } from "src/utils";
import { Invite as StyledInvite, styles } from "./styles";

import type { RoomId } from "src/types";

type Props = {
  text?: string,
  roomId: RoomId,
  outerRef: any,
  handleClose: Function,
  sendUserInviteRequest: Function,
  roles: Array<{ id: number, title: string, label: string }>,
};

type State = {
  displayName: string,
  email: string,
  role: string,
  displayNameError: boolean,
};

class Invite extends Component<Props, State> {
  static defaultProps = {
    text: "",
  };

  state = {
    displayName: "",
    email: "",
    role: roleTitles.member,
    displayNameError: false,
  };

  componentDidMount() {
    const { text } = this.props;
    if (text && validateEmail(text)) {
      this.setState({ email: text });
    } else {
      this.setState({ displayName: text });
    }
  }

  handleKeyDown = (event: any) => {
    // Handle Enter
    if (event.keyCode === 13) {
      this.inviteUser(event);
    }
  };

  handleClickOutside = (e: any) => {
    const { outerRef, handleClose } = this.props;
    if (outerRef) {
      if (!outerRef.contains(e.target)) {
        handleClose();
      }
    }
  };

  handleChange = (e: any) => {
    const { value, id } = e.target;
    // Don't allow square brackets in the displayName input
    if (id === "displayName") {
      const { sanitizedValue, hasSquareBrackets } =
        sanitizeSquareBrackets(value);
      this.setState({
        [id]: sanitizedValue,
        displayNameError: hasSquareBrackets,
      });
    } else {
      this.setState({ [id]: value });
    }
  };

  handleRoleChange = (e: SyntheticInputEvent<HTMLSelectElement>) => {
    e.preventDefault();
    this.setState({ role: e.target.value });
  };

  inviteUser = (e: any) => {
    e.preventDefault();
    const { roomId, sendUserInviteRequest, handleClose } = this.props;
    const { email, displayName, role } = this.state;
    handleClose();
    sendUserInviteRequest({
      email,
      orgRole: role,
      chatRoomId: parseInt(roomId, 10),
      displayName,
    });
  };

  preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  render() {
    const { handleClose, roles } = this.props;
    const { email, displayName, role, displayNameError } = this.state;

    return (
      <RelativeBox onClick={this.preventDefault} onKeyDown={this.handleKeyDown}>
        <StyledInvite>
          <BlockFormGroup>
            <Label htmlFor="displayName">{i18n.t(k.FULL_NAME_OPTIONAL)}</Label>
            <Input
              id="displayName"
              type="text"
              onChange={this.handleChange}
              value={displayName}
              placeholder={i18n.t(k.FULL_NAME)}
              autoComplete="off"
              autoFocus
            />
            {displayNameError && (
              <Text variant="pill" color="red">
                {i18n.t(k.SQUARE_BRACKETS_NOT_ALLOWED)}
              </Text>
            )}
          </BlockFormGroup>

          <BlockFormGroup>
            <Label htmlFor="email">{i18n.t(k.EMAIL_ADDRESS1)}</Label>
            <Input
              id="email"
              type="email"
              onChange={this.handleChange}
              value={email}
              autoComplete="off"
              placeholder="email@company.com"
            />
          </BlockFormGroup>
          <BlockFormGroup>
            <Label>{i18n.t(k.SELECT_ROLE)}</Label>
            <Select
              value={role}
              onChange={this.handleRoleChange}
              size="sm"
              {...styles.roleSelect}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.title}>
                  {role.label}
                </option>
              ))}
            </Select>
          </BlockFormGroup>
          <Footer>
            <SecondaryButton type="button" onClick={handleClose}>
              {i18n.t(k.CANCEL)}
            </SecondaryButton>
            <Button
              type="button"
              onClick={this.inviteUser}
              disabled={email.length === 0}
            >
              {i18n.t(k.SEND_INVITE)}
            </Button>
          </Footer>
        </StyledInvite>
      </RelativeBox>
    );
  }
}

export default onClickOutside(Invite);
